<template>
    <div class="page-container">
        <div v-if="loading" class="loader-container">
            <div class="loader"></div>
        </div>

        <!-- Top Bar -->
        <div class="top-bar">
            <div class="top-bar-left">
                <h2 class="page-title">
                    <i class="tim-icons icon-coins"></i>
                    Uplate
                </h2>
                <span class="count-badge">{{ payments.length }}</span>
            </div>
            <div class="top-bar-right">
                <base-button type="success" @click="openPayment()" class="action-btn">
                    <i class="tim-icons icon-simple-add"></i> {{ $t('admin.payments.newPayment') }}
                </base-button>
            </div>
        </div>

        <!-- Filters -->
        <div class="filter-bar">
            <label class="filter-label">{{ $t('admin.payments.client') }}:</label>
            <select v-model="selectedClientId" class="filter-select" @change="getPayments">
                <option value="">{{ $t('invoices.filters.all') }}</option>
                <option v-for="c in clients" :key="c.id" :value="c.id">
                    {{ c.name }} {{ c.tin ? '(' + c.tin + ')' : '' }}
                </option>
            </select>
            <base-button v-if="selectedClientId" type="default" size="sm" @click="selectedClientId = ''; getPayments()" class="clear-btn">
                <i class="tim-icons icon-simple-remove"></i> {{ $t('pos.receipt.clear') }}
            </base-button>
        </div>

        <!-- Table -->
        <div class="table-section">
            <div class="table-container">
                <table class="custom-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>{{ $t('common.date') }}</th>
                            <th>{{ $t('admin.payments.client') }}</th>
                            <th>{{ $t('admin.payments.amount') }} (RSD)</th>
                            <th>{{ $t('nav.esirs') }}</th>
                            <th>{{ $t('admin.clients.pricePerEsir') }}</th>
                            <th>{{ $t('admin.payments.periodFrom') }} - {{ $t('admin.payments.periodTo') }}</th>
                            <th>{{ $t('admin.payments.note') }}</th>
                            <th>{{ $t('apiTokens.table.createdBy') }}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(p, index) in payments" :key="p.id">
                            <td>{{ index + 1 }}</td>
                            <td class="small-text">{{ formatDate(p.timeStamp) }}</td>
                            <td class="name-cell">{{ p.client ? p.client.name : '-' }}</td>
                            <td><span class="mono amount">{{ formatNumber(p.amount) }}</span></td>
                            <td>{{ p.esirCount }}</td>
                            <td><span class="mono">{{ formatNumber(p.pricePerEsir) }}</span></td>
                            <td class="small-text">
                                <span v-if="p.periodFrom">{{ p.periodFrom }} → {{ p.periodTo }}</span>
                                <span v-else class="no-data">-</span>
                            </td>
                            <td class="small-text">{{ p.note || '-' }}</td>
                            <td class="small-text">{{ p.createdBy ? p.createdBy.name : '-' }}</td>
                            <td>
                                <base-button type="danger" icon size="sm" class="btn-link"
                                    @click="confirmDelete(p)" :title="$t('common.delete')">
                                    <i class="tim-icons icon-trash-simple"></i>
                                </base-button>
                            </td>
                        </tr>
                        <tr v-if="payments.length === 0">
                            <td colspan="10" class="empty-row">
                                <i class="tim-icons icon-zoom-split"></i>
                                <p>{{ $t('common.noData') }}</p>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot v-if="payments.length > 0">
                        <tr class="total-row">
                            <td colspan="3"><strong>{{ $t('admin.payments.total') }}:</strong></td>
                            <td><span class="mono amount">{{ formatNumber(totalAmount) }}</span></td>
                            <td colspan="6"></td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>

        <!-- New Payment Modal -->
        <transition name="modal-fade">
            <div v-if="showPayModal" class="modal-overlay" @click="showPayModal = false">
                <div class="modal-container" @click.stop>
                    <div class="modal-header" style="background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%);">
                        <h3>
                            <i class="tim-icons icon-coins"></i>
                            Nova uplata
                        </h3>
                        <button @click="showPayModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <div class="form-grid">
                            <div class="form-group full-width">
                                <label class="form-label">{{ $t('admin.payments.client') }} *</label>
                                <select v-model="payForm.clientId" class="form-select" @change="onClientSelect">
                                    <option value="">{{ $t('admin.payments.client') }}</option>
                                    <option v-for="c in clients" :key="c.id" :value="c.id">
                                        {{ c.name }} ({{ c.esirCount }} ESIR-ova)
                                    </option>
                                </select>
                            </div>
                            <div class="form-group full-width" v-if="payClientInfo">
                                <div class="auto-calc">
                                    {{ payClientInfo.esirCount }} ESIR-ova × {{ formatNumber(payClientInfo.pricePerEsir) }} RSD =
                                    <strong>{{ formatNumber(payClientInfo.esirCount * payClientInfo.pricePerEsir) }} RSD</strong>
                                </div>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.payments.amount') }} (RSD)</label>
                                <base-input v-model="payForm.amount" type="number" placeholder="Automatski ako prazno"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.payments.periodFrom') }}</label>
                                <base-input v-model="payForm.periodFrom" type="date"></base-input>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ $t('admin.payments.periodTo') }}</label>
                                <base-input v-model="payForm.periodTo" type="date"></base-input>
                            </div>
                            <div class="form-group full-width">
                                <label class="form-label">{{ $t('admin.payments.note') }}</label>
                                <textarea v-model="payForm.note" class="form-textarea" rows="2" placeholder="Npr. uplata za mart 2026"></textarea>
                            </div>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <base-button type="success" @click="savePayment()">
                            <i class="tim-icons icon-check-2"></i> Evidentiraj uplatu
                        </base-button>
                        <base-button type="default" @click="showPayModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Delete Confirmation -->
        <transition name="modal-fade">
            <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
                <div class="modal-container modal-sm" @click.stop>
                    <div class="modal-header danger-header">
                        <h3><i class="tim-icons icon-alert-circle-exc"></i> Brisanje</h3>
                        <button @click="showDeleteModal = false" class="modal-close">&times;</button>
                    </div>
                    <div class="modal-body">
                        <p class="confirm-text">
                            {{ $t('common.delete') }}?
                            <strong>{{ deleteTarget ? formatNumber(deleteTarget.amount) + ' RSD' : '' }}</strong>?
                        </p>
                    </div>
                    <div class="modal-footer">
                        <base-button type="danger" @click="deletePayment()">
                            <i class="tim-icons icon-trash-simple"></i> {{ $t('common.delete') }}
                        </base-button>
                        <base-button type="default" @click="showDeleteModal = false">
                            {{ $t('common.cancel') }}
                        </base-button>
                    </div>
                </div>
            </div>
        </transition>

        <!-- Toast -->
        <transition name="notif-fade">
            <div v-if="toast.message" :class="['toast-notif', toast.type]" @click="toast.message = ''">
                <i :class="toast.type === 'success' ? 'tim-icons icon-check-2' : 'tim-icons icon-alert-circle-exc'"></i>
                <span>{{ toast.message }}</span>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from '@/util/axiosinstance'

export default {
    name: 'AdminPayments',
    data() {
        return {
            loading: false,
            payments: [],
            clients: [],
            selectedClientId: '',
            showPayModal: false,
            showDeleteModal: false,
            deleteTarget: null,
            payForm: {},
            payClientInfo: null,
            toast: { message: '', type: 'success' }
        }
    },
    computed: {
        totalAmount() {
            return this.payments.reduce((s, p) => s + Number(p.amount || 0), 0)
        }
    },
    created() {
        this.getClients()
        this.getPayments()
    },
    methods: {
        getClients() {
            axios.get('/api/v1/admin/clients').then(res => {
                this.clients = res.data.filter(c => c.id !== 'SYSTEM')
            })
        },

        getPayments() {
            this.loading = true
            let url = '/api/v1/admin/payments'
            if (this.selectedClientId) {
                url += '?clientId=' + this.selectedClientId
            }
            axios.get(url).then(res => {
                this.loading = false
                this.payments = res.data
            }).catch(err => {
                this.loading = false
                this.showToast(this.$t('pos.result.error'), 'error')
            })
        },

        openPayment() {
            const today = new Date()
            const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, today.getDate())
            this.payForm = {
                clientId: this.selectedClientId || '',
                amount: '',
                periodFrom: today.toISOString().split('T')[0],
                periodTo: nextMonth.toISOString().split('T')[0],
                note: ''
            }
            this.payClientInfo = null
            if (this.payForm.clientId) {
                this.onClientSelect()
            }
            this.showPayModal = true
        },

        onClientSelect() {
            this.payClientInfo = this.clients.find(c => c.id === this.payForm.clientId) || null
        },

        savePayment() {
            if (!this.payForm.clientId) {
                this.showToast(this.$t('admin.payments.client'), 'error')
                return
            }
            this.loading = true
            axios.post('/api/v1/admin/payment', {
                clientId: this.payForm.clientId,
                amount: this.payForm.amount || null,
                periodFrom: this.payForm.periodFrom || null,
                periodTo: this.payForm.periodTo || null,
                note: this.payForm.note || null
            }).then(res => {
                this.loading = false
                this.showPayModal = false
                this.showToast(this.$t('common.save'), 'success')
                this.getPayments()
                this.getClients()
            }).catch(err => {
                this.loading = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        confirmDelete(payment) {
            this.deleteTarget = payment
            this.showDeleteModal = true
        },

        deletePayment() {
            this.loading = true
            axios.delete('/api/v1/admin/payment/' + this.deleteTarget.id).then(res => {
                this.loading = false
                this.showDeleteModal = false
                this.showToast(this.$t('common.delete'), 'success')
                this.getPayments()
            }).catch(err => {
                this.loading = false
                this.showDeleteModal = false
                const msg = err.response && err.response.data ? err.response.data.error : this.$t('pos.result.error')
                this.showToast(msg, 'error')
            })
        },

        formatNumber(val) {
            if (!val && val !== 0) return '0.00'
            return Number(val).toLocaleString('sr-RS', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        },

        formatDate(d) {
            if (!d) return '-'
            return new Date(d).toLocaleDateString('sr-RS')
        },

        showToast(message, type) {
            this.toast = { message, type }
            setTimeout(() => { this.toast = { message: '', type: 'success' } }, 3000)
        }
    }
}
</script>

<style scoped>
.page-container { padding: 0; }
.loader-container {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(255,255,255,0.7); display: flex;
    align-items: center; justify-content: center; z-index: 9999;
}
.loader {
    width: 40px; height: 40px;
    border: 4px solid #e9ecef; border-top-color: #1a73e8;
    border-radius: 50%; animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

.top-bar {
    display: flex; justify-content: space-between;
    align-items: center; margin-bottom: 15px; flex-wrap: wrap; gap: 10px;
}
.top-bar-left { display: flex; align-items: center; gap: 10px; }
.top-bar-right { display: flex; gap: 8px; }
.page-title {
    margin: 0; font-size: 22px; font-weight: 700;
    color: #2c3e50; display: flex; align-items: center; gap: 8px;
}
.page-title i { color: #28a745; }
.count-badge {
    background: #28a745; color: white;
    padding: 2px 10px; border-radius: 12px;
    font-size: 13px; font-weight: 600;
}
.action-btn {
    padding: 8px 16px !important; font-size: 13px !important;
    font-weight: 600 !important; display: flex;
    align-items: center; gap: 6px;
}

/* Filter */
.filter-bar {
    display: flex; align-items: center; gap: 10px;
    margin-bottom: 15px; padding: 12px 15px;
    background: #ffffff; border-radius: 10px;
    border: 1px solid #e0e0e0;
}
.filter-label { font-size: 13px; font-weight: 600; color: #495057; white-space: nowrap; }
.filter-select {
    padding: 8px 12px; border: 2px solid #dee2e6; border-radius: 8px;
    background: white; font-size: 14px; color: #2c3e50;
    cursor: pointer; min-width: 250px;
}
.filter-select:focus { outline: none; border-color: #1a73e8; }
.clear-btn { font-size: 12px !important; padding: 6px 10px !important; }

/* Table */
.table-section {
    background: #ffffff; border-radius: 12px;
    border: 1px solid #e0e0e0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.06); overflow: hidden;
}
.table-container { overflow-x: auto; }
.custom-table { width: 100%; border-collapse: collapse; }
.custom-table th, .custom-table td {
    padding: 10px 12px; text-align: left;
    border-bottom: 1px solid #e9ecef;
    color: #2c3e50; font-size: 13px;
}
.custom-table th {
    background: #f8f9fa; font-weight: 600;
    font-size: 11px; text-transform: uppercase;
    letter-spacing: 0.3px; color: #495057;
}
.custom-table tbody tr:hover { background: #f1f5ff; }
.total-row {
    background: #f8f9fa;
}
.total-row td { border-bottom: none; font-weight: 600; }
.name-cell { font-weight: 600; }
.small-text { font-size: 12px; color: #6c757d; }
.mono {
    font-family: monospace; background: #f1f3f5;
    padding: 2px 6px; border-radius: 4px; font-size: 12px;
}
.amount { font-weight: 600; color: #28a745; }
.no-data { color: #adb5bd; }
.empty-row { text-align: center; padding: 30px !important; color: #6c757d; }
.empty-row i { font-size: 30px; display: block; margin-bottom: 8px; opacity: 0.4; }

/* Auto calc */
.auto-calc {
    background: #e7f3ff; padding: 12px 16px; border-radius: 8px;
    font-size: 14px; color: #1a73e8; text-align: center;
}
.auto-calc strong { font-size: 16px; }

/* Modals */
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity 0.3s ease; }
.modal-fade-enter, .modal-fade-leave-to { opacity: 0; }
.modal-overlay {
    position: fixed; top: 0; left: 0; right: 0; bottom: 0;
    background: rgba(0,0,0,0.5); display: flex;
    align-items: center; justify-content: center;
    z-index: 99999; backdrop-filter: blur(3px);
}
.modal-container {
    background: #ffffff; border-radius: 14px;
    max-width: 650px; width: 95%; max-height: 90vh;
    overflow: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.25);
}
.modal-sm { max-width: 440px; }
.modal-header {
    background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%);
    padding: 18px 20px; display: flex;
    justify-content: space-between; align-items: center;
    border-radius: 14px 14px 0 0;
}
.modal-header h3 {
    margin: 0; color: white; font-size: 17px;
    font-weight: 600; display: flex; align-items: center; gap: 8px;
}
.danger-header { background: linear-gradient(135deg, #dc3545 0%, #bd2130 100%) !important; }
.modal-close {
    background: none; border: none; color: white;
    font-size: 26px; cursor: pointer;
    width: 30px; height: 30px;
    display: flex; align-items: center; justify-content: center;
    border-radius: 50%; transition: background 0.2s;
}
.modal-close:hover { background: rgba(255,255,255,0.2); }
.modal-body { padding: 20px; color: #2c3e50; }
.modal-footer {
    padding: 14px 20px; background: #f8f9fa;
    border-top: 1px solid #e9ecef;
    display: flex; gap: 10px; justify-content: flex-end;
}
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
.form-group { margin-bottom: 0; }
.full-width { grid-column: 1 / -1; }
.form-label {
    display: block; font-size: 13px; font-weight: 600;
    color: #495057; margin-bottom: 5px;
}
.form-select {
    width: 100%; padding: 10px 12px;
    border: 2px solid #dee2e6; border-radius: 8px;
    background: white; font-size: 14px; color: #2c3e50;
    cursor: pointer;
}
.form-select:focus { outline: none; border-color: #1a73e8; }
.form-textarea {
    width: 100%; padding: 10px 12px;
    border: 2px solid #dee2e6; border-radius: 8px;
    font-size: 14px; color: #2c3e50; resize: vertical;
    font-family: inherit;
}
.form-textarea:focus { outline: none; border-color: #1a73e8; }
.confirm-text { text-align: center; font-size: 15px; color: #2c3e50; margin: 10px 0; }
.confirm-text strong { color: #dc3545; }

.toast-notif {
    position: fixed; top: 25px; right: 25px;
    padding: 12px 20px; border-radius: 10px;
    display: flex; align-items: center; gap: 8px;
    font-size: 14px; font-weight: 500;
    z-index: 100000; cursor: pointer;
    box-shadow: 0 4px 15px rgba(0,0,0,0.15); color: white;
}
.toast-notif.success { background: #28a745; }
.toast-notif.error { background: #dc3545; }
.notif-fade-enter-active, .notif-fade-leave-active { transition: all 0.3s ease; }
.notif-fade-enter, .notif-fade-leave-to { opacity: 0; transform: translateX(50px); }

@media (max-width: 768px) {
    .form-grid { grid-template-columns: 1fr; }
    .filter-bar { flex-wrap: wrap; }
    .filter-select { min-width: auto; flex: 1; }
}
</style>